import { useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';
import {
  OUTCOME,
  TRIGGER_LABEL,
  isShownByDefault,
  plainRunError,
  relativeTime,
  taskAttemptError,
  taskStateText,
} from '@/modules/agents/utils/agentStatusLabels';
import type { AgentTaskRow, AgentTaskState, AgentTimelineEntry } from '../../api/agentStatusTypes';
import {
  useAgentStatus,
  useAgentTaskTimeline,
  useCloseAgentTask,
} from '../../hooks/useCommandCenter';
import { TONE_CLASS } from './tone';
import styles from './AgentStatus.module.css';

/**
 * The agent's work at one location, one row per task: where it stands, the last attempt and when
 * it tries next. A row opens to the task's timeline. `focusTaskId` opens and scrolls to a task
 * (a "failed N times in a row" reason on the status line points here).
 */
export function AgentTasks({
  practiceId,
  locationId,
  roleKey,
  agentName,
  focusTaskId,
  onOpenRun,
}: {
  practiceId: string;
  locationId: string;
  roleKey: string;
  agentName: string;
  focusTaskId: string | null;
  onOpenRun: (runLogId: string) => void;
}) {
  const [showAll, setShowAll] = useState(false);
  const status = useAgentStatus(practiceId, locationId, roleKey, showAll);
  const now = Date.now();
  const tasks = (status.data?.tasks ?? []).filter((row) => showAll || isShownByDefault(row));
  const hidden = showAll ? 0 : (status.data?.tasks.length ?? 0) - tasks.length;

  return (
    <>
      <div className={styles.tasksToolbar}>
        <span>
          {hidden > 0 ? `${hidden} scheduled task${hidden === 1 ? '' : 's'} hidden` : ' '}
        </span>
        <label className={styles.toggle}>
          <input
            type="checkbox"
            checked={showAll}
            onChange={(event) => setShowAll(event.target.checked)}
          />
          Show scheduled and finished
        </label>
      </div>
      {status.isLoading && <p className={styles.quiet}>Loading tasks…</p>}
      {!status.isLoading && !status.data?.run && (
        <p className={styles.quiet}>{agentName}'s tasks are unavailable.</p>
      )}
      {status.data?.run && tasks.length === 0 && (
        <p className={styles.quiet}>No open work. {agentName} is not working on anything here.</p>
      )}
      {tasks.length > 0 && (
        <div className={styles.tableScroll}>
          <div className={styles.taskTable} role="table" aria-label={`${agentName}'s tasks`}>
            <div className={styles.taskHeader} role="row">
              <span role="columnheader">Task</span>
              <span role="columnheader">State</span>
              <span role="columnheader">Last attempt</span>
              <span role="columnheader">Next try</span>
            </div>
            {tasks.map((row) => (
              <TaskItem
                key={row.taskId}
                row={row}
                now={now}
                practiceId={practiceId}
                locationId={locationId}
                focused={row.taskId === focusTaskId}
                agentName={agentName}
                onOpenRun={onOpenRun}
              />
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function TaskItem({
  row,
  now,
  practiceId,
  locationId,
  focused,
  agentName,
  onOpenRun,
}: {
  row: AgentTaskRow;
  now: number;
  practiceId: string;
  locationId: string;
  focused: boolean;
  agentName: string;
  onOpenRun: (runLogId: string) => void;
}) {
  const [open, setOpen] = useState(focused);
  const ref = useRef<HTMLDivElement>(null);
  const state = taskStateText(row);
  const attempt = row.lastAttempt;
  const attemptError = attempt?.error
    ? taskAttemptError(row, attempt.error, agentName, attempt.draftSaved)
    : undefined;

  useEffect(() => {
    if (!focused) return;
    setOpen(true);
    ref.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [focused]);

  return (
    <div
      ref={ref}
      className={clsx(styles.taskItem, focused && styles.taskItemFocused)}
      role="rowgroup"
    >
      <button
        type="button"
        className={styles.taskRow}
        role="row"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span className={styles.cellStack} role="cell">
          <span className={styles.taskName}>{row.title}</span>
          {row.drafts > 0 && (
            <span className={styles.cellMuted}>
              {row.drafts} draft{row.drafts === 1 ? '' : 's'}
            </span>
          )}
        </span>
        <span role="cell">
          <span className={TONE_CLASS[state.tone]}>{state.label}</span>
        </span>
        <span className={styles.cellStack} role="cell">
          {attempt ? (
            <>
              <span className={styles.cellMuted}>
                {relativeTime(attempt.startedAt, now)} · {OUTCOME[attempt.outcome].label}
              </span>
              {attemptError && (
                <span className={attemptError.past ? styles.cellMuted : styles.cellError}>
                  {attemptError.text}
                </span>
              )}
            </>
          ) : (
            <span className={styles.cellMuted}>Not tried yet</span>
          )}
        </span>
        <span className={styles.cellMuted} role="cell">
          {row.nextAttemptAt ? relativeTime(row.nextAttemptAt, now) : '—'}
        </span>
      </button>
      {open && CLOSABLE_STATES.has(row.state) && (
        <CloseTask practiceId={practiceId} locationId={locationId} taskId={row.taskId} />
      )}
      {open && (
        <TaskTimeline
          practiceId={practiceId}
          locationId={locationId}
          taskId={row.taskId}
          now={now}
          agentName={agentName}
          onOpenRun={onOpenRun}
        />
      )}
    </div>
  );
}

/** Open work an operator can close here. Work the agent is on right now finishes first. */
const CLOSABLE_STATES: ReadonlySet<AgentTaskState> = new Set<AgentTaskState>([
  'queued',
  'failing',
  'awaiting_approval',
  'held',
]);

/**
 * Close one open task, whatever the practice's list shows for it: a task held here and missing from
 * the practice's list blocked its schedule's Run now with nothing to click (Jenny's strategy review,
 * Little Heroes 2026-11-03). The practice sees it as withdrawn, out of date.
 */
function CloseTask({
  practiceId,
  locationId,
  taskId,
}: {
  practiceId: string;
  locationId: string;
  taskId: string;
}) {
  const [confirming, setConfirming] = useState(false);
  const close = useCloseAgentTask(practiceId, locationId);
  const error = close.isError
    ? apiErrorMessage(close.error, 'This task could not be closed. Try again.')
    : null;
  if (!confirming) {
    return (
      <div className={styles.taskActions}>
        <button type="button" className={styles.linkButton} onClick={() => setConfirming(true)}>
          Close this task
        </button>
      </div>
    );
  }
  return (
    <div className={styles.taskActions}>
      <span>
        Close it? The practice sees it as withdrawn, out of date, and the schedule can run again.
      </span>
      <button
        type="button"
        className={styles.linkButton}
        disabled={close.isPending}
        onClick={() => close.mutate(taskId)}
      >
        {close.isPending ? 'Closing…' : 'Close it'}
      </button>
      <button
        type="button"
        className={styles.linkButton}
        disabled={close.isPending}
        onClick={() => {
          close.reset();
          setConfirming(false);
        }}
      >
        Keep it
      </button>
      {error && <span className={styles.taskActionError}>{error}</span>}
    </div>
  );
}

function apiErrorMessage(err: unknown, fallback: string): string {
  const response = (err as { response?: { data?: { error?: { message?: unknown } } } })?.response;
  const message = response?.data?.error?.message;
  return typeof message === 'string' && message ? message : fallback;
}

const SOURCE_LABEL: Record<string, string> = {
  work_schedule: 'Created by a schedule',
  owner_message: 'Created by the owner',
};

function TaskTimeline({
  practiceId,
  locationId,
  taskId,
  now,
  agentName,
  onOpenRun,
}: {
  practiceId: string;
  locationId: string;
  taskId: string;
  now: number;
  agentName: string;
  onOpenRun: (runLogId: string) => void;
}) {
  const timeline = useAgentTaskTimeline(practiceId, locationId, taskId, true);
  if (timeline.isLoading)
    return <p className={clsx(styles.quiet, styles.timeline)}>Loading history…</p>;
  if (timeline.isError || !timeline.data) {
    return (
      <p className={clsx(styles.quiet, styles.timeline)}>
        This task's history could not be loaded.
      </p>
    );
  }
  return (
    <ol className={styles.timeline}>
      {timeline.data.map((entry, index) => (
        <li key={`${entry.kind}:${entry.at}:${index}`} className={styles.timelineEntry}>
          <span title={new Date(entry.at).toLocaleString()}>{relativeTime(entry.at, now)}</span>
          <span className={styles.timelineBody}>
            <TimelineBody entry={entry} now={now} agentName={agentName} onOpenRun={onOpenRun} />
          </span>
        </li>
      ))}
    </ol>
  );
}

function TimelineBody({
  entry,
  now,
  agentName,
  onOpenRun,
}: {
  entry: AgentTimelineEntry;
  now: number;
  agentName: string;
  onOpenRun: (runLogId: string) => void;
}) {
  switch (entry.kind) {
    case 'created':
      if (!entry.source) return <>Created</>;
      if (SOURCE_LABEL[entry.source]) return <>{SOURCE_LABEL[entry.source]}</>;
      // Anything else is a data source whose new data created the task.
      return (
        <>
          Created from new data <span className={styles.mono}>{entry.source}</span>
        </>
      );
    case 'run': {
      const outcome = OUTCOME[entry.run.outcome];
      return (
        <>
          <span className={TONE_CLASS[outcome.tone]}>{outcome.label}</span>
          <span>Started by {TRIGGER_LABEL[entry.run.trigger]}</span>
          <button
            type="button"
            className={styles.linkButton}
            onClick={() => onOpenRun(entry.run.id)}
          >
            View run
          </button>
          {entry.run.error && (
            <span className={styles.timelineError}>
              {plainRunError(entry.run.error, agentName, entry.run.draftSaved)}
            </span>
          )}
        </>
      );
    }
    case 'wait':
      return (
        <>
          {entry.until ? `Waits until ${relativeTime(entry.until, now)}` : 'Waits'}
          {entry.status !== 'pending' && <span className={styles.mono}>({entry.status})</span>}
          {entry.reason && <span className={styles.timelineQuote}>{entry.reason}</span>}
        </>
      );
    case 'draft':
      return <>Draft v{entry.version}</>;
    case 'message':
      return (
        <>
          {entry.direction === 'inbound' ? 'Owner wrote' : 'Agent wrote'}
          <span className={styles.mono}>{entry.messageKind.replace(/_/g, ' ')}</span>
          <span className={styles.timelineQuote}>{entry.text}</span>
        </>
      );
  }
}
